import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IRootState } from 'src/reducers';
import selectors from 'src/selectors';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import useFetch from 'use-http';

interface IAPVoucherReportDialogProps {
    dialogName: string;
    voucherNumber?: string;
    hideDialog: () => void;
    // Set from the Report Dashboard, where there's no voucher already open on a page for "Current
    // Voucher" (:reports.type = 'C') to target - only the Invoice Date / Check Date listing modes
    // make sense there, so that option is dropped from the list entirely rather than left selectable
    // with nothing behind it.
    hideCurrentVoucherOption?: boolean;
}

const downloadBlobResponseAsFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// Port of ap_voucher.fmt's Reports window (AP_VOUCHER.RDF). "Current Voucher" is the form's own
// :reports.type = 'C', which always targets the voucher already open on the page. "Invoice Date" /
// "Check Date" are :reports.type = 'O' / 'CD' - listing modes over a date range, matching
// usp_APVoucherReportHeader's own Type values. The "Other Parameters" label prefix Oracle used for
// the latter two is dropped per explicit confirmation - it added nothing here.
const APVoucherReportDialog = (props: IAPVoucherReportDialogProps) => {
    const { register, errors, getValues, watch, trigger, setValue } = useForm();
    const { post, response } = useFetch();
    const [loading, setLoading] = useState<boolean>(false);
    const isOpen = useSelector((state: IRootState) => selectors.layout.isDialogShowing(state, props.dialogName));
    const defaultReportType = props.hideCurrentVoucherOption ? 'O' : 'C';
    const reportType = watch('reportType', defaultReportType);
    const reportTypeOptions = props.hideCurrentVoucherOption
        ? [
            { value: 'O', label: 'Invoice Date' },
            { value: 'CD', label: 'Check Date' }
        ]
        : [
            { value: 'C', label: 'Current Voucher' },
            { value: 'O', label: 'Invoice Date' },
            { value: 'CD', label: 'Check Date' }
        ];

    // This component never unmounts between opens (Dialog's CSSTransition only removes its own
    // children's DOM), so reset back to the default and clear any leftover range/vendor values from
    // a prior open each time the dialog reopens.
    useEffect(() => {
        if (isOpen) {
            setValue('reportType', defaultReportType);
            setValue('vendorNumber', '');
            setValue('beginDate', '');
            setValue('endDate', '');
        }
    }, [isOpen]);

    const OKClicked = async () => {
        const values = getValues();

        if (values.reportType !== 'C') {
            const isValid = await trigger(['beginDate', 'endDate']);
            if (!isValid) {
                return;
            }
        }

        setLoading(true);
        try {
            const payload = values.reportType === 'C'
                ? { voucherNumber: props.voucherNumber }
                : {
                    type: values.reportType,
                    beginDate: values.beginDate,
                    endDate: values.endDate,
                    vendorNumber: values.vendorNumber
                };
            const fileName = values.reportType === 'C'
                ? `AP-Voucher-${props.voucherNumber}.pdf`
                : 'AP-Voucher-Report.pdf';

            await post('/v1.0/reports/ap-voucher', payload);
            if (response.ok) {
                const blob = await response.blob();
                downloadBlobResponseAsFile(blob, fileName);
            } else {
                console.log('Unable to generate AP Voucher report.');
            }
        } catch (e) {
            console.log('Unable to generate AP Voucher report.', e);
        }
        setLoading(false);

        props.hideDialog();
    };

    return (
        <Dialog title="Print Voucher Report" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '30%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form style={{ width: '85%' }}>
                    <RHFSelect
                        label="Report Type"
                        labelOnLeft={true}
                        labelStyle={{ whiteSpace: 'nowrap', marginRight: '1rem' }}
                        name="reportType"
                        options={reportTypeOptions}
                        defaultVal={defaultReportType}
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                    />
                    {reportType === 'C' && (
                        <div className="form-field" style={{ whiteSpace: 'nowrap' }}>
                            Voucher #: {props.voucherNumber}
                        </div>
                    )}
                    {reportType !== 'C' && <>
                        <RHFInputBox
                            label="Vendor No."
                            labelOnLeft={true}
                            labelStyle={{ whiteSpace: 'nowrap', marginRight: '1rem' }}
                            name="vendorNumber"
                            type="number"
                            register={register}
                            errors={errors}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Beginning Date"
                            labelOnLeft={true}
                            labelStyle={{ whiteSpace: 'nowrap', marginRight: '1rem' }}
                            name="beginDate"
                            type="date"
                            register={register}
                            errors={errors}
                            rules={{ required: 'Beginning Date is required.' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Ending Date"
                            labelOnLeft={true}
                            labelStyle={{ whiteSpace: 'nowrap', marginRight: '1rem' }}
                            name="endDate"
                            type="date"
                            register={register}
                            errors={errors}
                            rules={{ required: 'Ending Date is required.' }}
                            style={{ width: '100%' }}
                        />
                    </>}
                </form>
            </div>
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={loading ? 'Generating' : 'OK'}
                    disabled={loading}
                    loading={loading}
                    onClick={() => !loading && OKClicked()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Cancel'}
                    disabled={loading}
                    onClick={() => props.hideDialog()}
                />
            </div>
        </Dialog>
    );
};

export default APVoucherReportDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/dialogs/APVoucherReportDialog.tsx